<script module lang="ts">
	export interface Section {
		// Also the discriminator a consumer's renderTags snippet matches on, so
		// it stays stable even when the visible label differs (see displayTitle).
		title: string;
		// Label rendered beside the section's results, when it should differ from
		// `title`. An empty string renders no label at all — used where the
		// results themselves already carry the section's name (news' HENI pill).
		displayTitle?: string;
		variant: 'default' | 'tags';
		results: SearchResultProps[];
		// When set (and the consumer passes viewAllResultsHref), a "See all"
		// link renders beside the section title pointing at
		// `<viewAllResultsHref>?searchValue=<term>#<viewAllAnchor>`.
		viewAllAnchor?: string;
	}
	type OptionClasses = Partial<Section>;
	export interface SearchClasses {
		option?: OptionClasses;
		selectedOption?: OptionClasses;
		noResults?: string;
		list?: string;
		mobileOverlay?: string;
		mobileList?: string;
		// Merged onto the root of both the mobile and desktop SearchResultsList,
		// e.g. to override the default vertical padding.
		resultsList?: string;
		searchIcon?: string;
		input?: string;
		tagTitle?: string;
		// Merged onto the default (non-tags) section title block, e.g. to
		// override its default vertical margins.
		sectionTitle?: string;
		// Merged onto the results column of each default (non-tags) section —
		// the SearchResult rows and their dividers, not the section title —
		// e.g. to inset the rows from the viewport edge on mobile. Receives the
		// section so consumers can treat individual sections differently.
		sectionResults?: (section: Section) => string;
		section?: (i: number) => string;
	}
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { fly } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import { ClearIcon } from '../../../../../assets/icons/ClearIcon';
	import { SearchIcon } from '../../../../../assets/icons/SearchIcon';
	import type { SearchCombobox } from '../../../utils/createSearchCombobox';
	import type { SearchResultProps } from '../SearchResult/SearchResult.svelte';
	import type { SearchResultsList as SearchResultsListComponent } from '../SearchResultsList';

	let {
		ready = true,
		dataCy,
		placeholder,
		ariaLabel = undefined,
		error = '',
		sections = [],
		loading = false,
		value,
		classes = {},
		noResultsMessage = 'No results found',
		onCloseSearch = undefined,
		minSearchLength = 0,
		combobox,
		showOverlay = undefined,
		viewAllResultsHref = undefined,
		goto = undefined,
		renderTags = undefined,
		renderHeader = undefined,
		onclick,
		onkeyup,
		onEnter = undefined,
		...rest
	}: Props = $props();

	interface Props {
		ready?: boolean | undefined;
		dataCy: string;
		placeholder: string;
		ariaLabel?: string | undefined;
		error?: string | undefined;
		sections?: Section[];
		loading?: boolean;
		value: Writable<string>;
		classes?: SearchClasses | undefined;
		noResultsMessage?: string | undefined;
		onCloseSearch?: (() => void) | undefined;
		// Enter is inert below this many (trimmed) characters, mirroring the
		// consumer's debounced-query threshold (see QuerySearch).
		minSearchLength?: number;
		combobox: SearchCombobox;
		showOverlay?: boolean | undefined;
		viewAllResultsHref?: string | undefined;
		goto?: ((href: string) => void) | undefined;
		renderTags?:
			| import('svelte').Snippet<
					[{ section: Section; onCloseSearch: () => void; lastItem: boolean }]
			  >
			| undefined;
		renderHeader?:
			import('svelte').Snippet<[{ searchValue: string; onCloseSearch: () => void }]> | undefined;
		class?: string;
		onclick?: () => void;
		onkeyup?: (e: Event) => void;
		// Pressing Enter with a non-empty input closes the search and calls this
		// with the live (non-debounced) input value. Takes precedence over the
		// viewAllResultsHref navigation.
		onEnter?: ((searchValue: string) => void) | undefined;
	}

	let dataCyPrefix = $derived(`${dataCy}-search`);

	let open = $derived(combobox.states.open);

	$effect(() => {
		open.set(!!$value);
	});

	// The results list only shows once a term has been typed (after the
	// debounce) and is waiting on a request anyway, so it is imported on first
	// use rather than preloaded on every page with a search box. Focusing or
	// hovering the input starts the import well before the list can open.
	let SearchResultsList = $state<typeof SearchResultsListComponent>();

	const loadResultsList = () => {
		if (SearchResultsList) return;

		void import('../SearchResultsList').then((module) => {
			SearchResultsList = module.SearchResultsList;
		});
	};

	$effect(() => {
		if ($value) loadResultsList();
	});

	const handleInputChange = (event: {
		target: (EventTarget & { value?: string | undefined }) | null;
	}) => {
		const newValue = (event.target as HTMLInputElement).value;

		if (newValue.length === 0) {
			value.set('');
		}
	};

	const handleClear = () => {
		value.set('');
		if (inputRef) {
			inputRef.value = '';
		}
		open.set(false);
	};

	const handleCloseSearch = () => {
		if (onCloseSearch) {
			onCloseSearch();
		}
		open.set(false);
		value.set('');
	};

	let inputRef: HTMLInputElement | undefined = $state();
	let showViewAllButton = $derived(sections.every((section) => section.results.length >= 5));

	const getHrefWithParams = (baseHref: string | undefined, searchValue: string | undefined) => {
		if (baseHref && searchValue) {
			const url = new URL(baseHref, window.location.origin);

			url.searchParams.set('searchValue', searchValue);
			return url.toString();
		}
	};

	let viewAllResultsHrefWithParams = $derived(
		getHrefWithParams(viewAllResultsHref, $value as string | undefined)
	);

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key !== 'Enter') {
			return;
		}

		const enteredValue = inputRef?.value.trim();

		// When a consumer provides onEnter it owns the Enter key entirely: no
		// falling through to the viewAllResultsHref navigation, whose debounced
		// term can be stale (e.g. backspaced below the minimum just before
		// pressing Enter).
		if (onEnter) {
			if (!enteredValue || enteredValue.length < minSearchLength) {
				return;
			}
			// Clear the DOM input as well as the store: the Enter keyup lands
			// after this and re-schedules the consumer's debounce with whatever
			// the input still holds, which would re-open the suggestions with the
			// stale term ~500ms after navigating.
			if (inputRef) {
				inputRef.value = '';
			}
			// Dismiss the on-screen keyboard, but leave the search open for the
			// consumer to tear down as its navigation settles. On iOS the return
			// key is a "Done"/"Search" key whose whole job is to put the keyboard
			// away; calling onCloseSearch here instead unmounted the mobile
			// overlay (and with it the results) a frame before the navigation
			// landed, which read as the results vanishing / the previous page
			// coming back (HB-936).
			inputRef?.blur();
			open.set(false);
			value.set('');
			onEnter(enteredValue);
			return;
		}

		if (viewAllResultsHrefWithParams) {
			open.set(false);
			if (goto) {
				goto(viewAllResultsHrefWithParams);
			}
		}
	};

	// Closing the results is interaction-driven, never blur-driven (HB-936): the
	// on-screen keyboard's "Done" blurs the input without the visitor choosing to
	// leave the search, and every attempt to special-case that blur by detecting
	// the device (a "Mobi" UA sniff, then a pointer-coarse media query) broke in
	// the HENI app's webview, whose desktop-mode content defeats both. A
	// pointerdown outside the search is unfakeable intent by comparison — and a
	// keyboard dismissal cannot produce one on any device.
	//
	// "Outside" means outside every root carrying this search's data-search-root
	// tag, not just this instance's: NavBar mounts the same search twice (mobile
	// SearchTopBar and BottomBar) sharing one combobox store, so one instance
	// closing on interaction with the other's dropdown would tear down both.
	$effect(() => {
		if (!$open) {
			return;
		}

		const handlePointerDownOutside = (event: Event) => {
			const target = event.target;
			if (target instanceof Element && target.closest(`[data-search-root="${dataCyPrefix}"]`)) {
				return;
			}
			open.set(false);
		};

		document.addEventListener('pointerdown', handlePointerDownOutside, true);
		return () => {
			document.removeEventListener('pointerdown', handlePointerDownOutside, true);
		};
	});

	onMount(() => {
		// Tailwind's `lg` breakpoint, written out: importing `tailwindcss/defaultTheme`
		// for this one value shipped the whole default theme (~26 KiB) to every page.
		const mediaQuery = window.matchMedia('(max-width: 64rem)');
		if (mediaQuery.matches && inputRef) {
			inputRef.focus();
		}
		if (inputRef) {
			inputRef.addEventListener('keydown', handleKeyDown);
		}
		return () => {
			if (inputRef) {
				inputRef.removeEventListener('keydown', handleKeyDown);
			}
		};
	});
</script>

<!--
	`min-w-0`: this box is `flex-grow` next to the nav's site-links column, and
	callers may drop the width cap entirely (`lg:max-w-none`). A flex item's default
	`min-width: auto` floors it at its content's intrinsic width, so without this it
	cannot give way once the neighbouring column needs the room.
-->
<div
	data-search-root={dataCyPrefix}
	class={twMerge('flex min-w-0 flex-grow flex-col lg:mx-8 lg:max-w-[27.5rem]', rest.class)}
>
	<div class="relative">
		<input
			class={twMerge(
				'py-3.75 font-silka w-full rounded pl-4 pr-10 text-[0.875rem] hover:border-gray-700 focus:border-gray-700 focus:outline-none lg:border lg:py-2 lg:pl-10',
				classes?.input
			)}
			{onclick}
			onpointerenter={loadResultsList}
			enterkeyhint="search"
			onfocus={() => {
				loadResultsList();
				if (!ready && inputRef) {
					inputRef.blur();
				}
			}}
			{onkeyup}
			onblur={(e) => {
				// A bare blur is never a choice to leave the search: dismissing the
				// on-screen keyboard (iOS's accessory-bar "Done") blurs the input
				// with no relatedTarget, and closing here made the results vanish
				// under the visitor (HB-936). Closing is driven by the
				// outside-pointerdown effect instead, which no keyboard dismissal
				// can trigger.
				if (e.relatedTarget && inputRef) {
					inputRef.focus();
				}
			}}
			oninput={handleInputChange}
			data-cy={`${dataCyPrefix}-input`}
			{placeholder}
			aria-label={ariaLabel || placeholder}
			bind:this={inputRef}
		/>
		<div
			class={twMerge(
				'absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 lg:block',
				classes?.searchIcon
			)}
		>
			<SearchIcon />
		</div>
		{#if $value}
			<button
				type="button"
				aria-label="Clear search"
				class="absolute right-2 top-1/2 z-10 -translate-y-1/2"
				onclick={handleClear}
			>
				<ClearIcon color="gray-200" />
			</button>
		{/if}
	</div>
</div>

{#if showOverlay && !$value}
	<div
		class="fixed left-0 top-[3rem] z-50 mx-auto h-[calc(100dvh-3rem)] w-full bg-gray-900 opacity-90 lg:hidden"
	></div>
{/if}

{#if $open}
	<div
		data-search-root={dataCyPrefix}
		class={twMerge('fixed-wrapper bg-gray-0 z-40 mx-auto lg:hidden', classes?.mobileOverlay)}
	>
		{#if $value && SearchResultsList}
			<SearchResultsList
				dataCy={dataCyPrefix}
				{error}
				{loading}
				{noResultsMessage}
				{classes}
				{sections}
				{viewAllResultsHrefWithParams}
				{showViewAllButton}
				{renderTags}
				{renderHeader}
				searchValue={$value}
				onCloseSearch={handleCloseSearch}
				class={twMerge(classes?.mobileList, classes?.resultsList)}
			/>
		{/if}
	</div>

	{#if $value && SearchResultsList}
		<ul
			data-search-root={dataCyPrefix}
			class={twMerge(
				'left bg-gray-0 absolute left-1/2 top-[2.5rem] -ml-[300px] mt-5 hidden max-h-[calc(100vh-9.375rem)] !w-[37.5rem] flex-col overflow-scroll overflow-x-hidden overscroll-contain rounded-lg border border-gray-200 shadow-md lg:flex',
				classes?.list
			)}
			transition:fly={{ duration: 150, y: -5 }}
		>
			<SearchResultsList
				dataCy={dataCyPrefix}
				{error}
				{loading}
				{noResultsMessage}
				{classes}
				{sections}
				{viewAllResultsHrefWithParams}
				{showViewAllButton}
				{renderTags}
				{renderHeader}
				searchValue={$value}
				onCloseSearch={handleCloseSearch}
				class={classes?.resultsList}
			/>
		</ul>
	{/if}
{/if}

<style>
	.fixed-wrapper {
		width: 100%;
		position: fixed;
		top: 3rem;
		left: 0px;
		transform: translate3d(0, 0, 0);
		overscroll-behavior: contain;
		overflow: scroll;
		height: calc(100dvh - 3rem);
		-webkit-overflow-scrolling: touch;
	}
</style>
